import * as _ from "lodash";

import type { IconName } from "@similarweb/icons";

import categoryService from "app/common/services/categoryService";
import { swSettings } from "app/common/services/swSettings";
import { ESortingType } from "app/components/filters-bar/country-filter/CountryFilterTypes";
import { EAdsTabs } from "app/components/React/PlaResearch/type";
import { BADGE_ITEMS } from "app/components/sidebar3/types";
import {
    WEBSITE_ANALYSIS_GEOGRAPHY,
    WEBSITE_ANALYSIS_MARKETING_CHANNELS,
    WEBSITE_ANALYSIS_MARKETING_DISPLAY_AD_NETWORKS,
    WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW,
    WEBSITE_ANALYSIS_MARKETING_PUBLISHER_OVERVIEW,
    WEBSITE_ANALYSIS_MARKETING_KEYWORDS,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_OVERVIEW,
    WEBSITE_ANALYSIS_MARKETING_REFERRAL_INCOMING,
    WEBSITE_ANALYSIS_MARKETING_REFERRAL_OUTGOING,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_ADS,
    WEBSITE_ANALYSIS_MARKETING_SOCIAL_OVERVIEW,
    WEBSITE_ANALYSIS_PERFORMANCE,
    WEBSITE_ANALYSIS_TRAFFIC_ENGAGEMENT,
} from "app/pages/sales-intelligence/constants/routes";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import { SwRedux } from "app/services/SwRedux";
import { ROUTES as advertiserActivityRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/advertiserActivity/advertiserActivityConstants";
import { ROUTES as displayAdvertisingRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingConstants";
import { ROUTES as publisherAnalysisRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisConstants";
import { ROUTES as searchAdvertisingRoutes } from "single-spa/react-app/config/solutions25/adIntelligence/searchAdvertising/searchAdvertisingConstants";

import { i18nFilter } from "../../filters/i18nFilter";
import type { ICountryObject } from "../../services/CountryService";
import CountryService from "../../services/CountryService";

import { FilterWebSourceType } from "./types";

export function isHasMobileWebData(duration: string, country: number | string, componentId = "MobileWeb"): boolean {
    const hasMobileWebData =
        swSettings.allowedDuration(duration, componentId) && swSettings.allowedCountry(country, componentId);
    return hasMobileWebData;
}

function isHasMobileWebDataIgnoreDuration(country: number | string, componentId = "MobileWeb"): boolean {
    const hasMobileWebData = swSettings.allowedCountry(country, componentId);
    return hasMobileWebData;
}

const isMobileWebEnabled = (component: string, country: number) => {
    const result = isHasMobileWebDataIgnoreDuration(country, component);
    return result;
};

const defaultResolver = (componentID: string, params: { duration: string; country: string }) => {
    const available = isHasMobileWebData(params.duration, params.country, componentID);
    return [
        commonWebSources.total(!available, available),
        commonWebSources.desktop(),
        commonWebSources.mobileWeb(!available, available),
    ];
};

export const commonWebSources = {
    // disabled = NOT has data AND NOT has permission
    // available = has data
    total: (disabled = false, available = true, props?: object) => ({
        id: FilterWebSourceType.Total,
        text: i18nFilter()("websources.total"),
        icon: "combined" as IconName,
        disabled,
        available,
        ...props,
    }),
    desktop: (disabled = false, available = true, props?: object) => ({
        id: FilterWebSourceType.Desktop,
        text: i18nFilter()("toggler.title.desktop"),
        icon: "desktop" as IconName,
        disabled,
        available,
        ...props,
    }),
    mobileWeb: (disabled = false, available = true, props?: object) => ({
        id: FilterWebSourceType.MobileWeb,
        text: i18nFilter()("toggler.title.mobile"),
        icon: "mobile-web" as IconName,
        disabled,
        available,
        ...props,
    }),
};

export const isSubdomainsFilterDisabled = (params: FixmeType, state: FixmeType): boolean => {
    // The next three lines are a temporary solution. They will be deprecated soon after the backends will fix the  SIM-28181 issue.
    const disabledTabsArray = ["plaResearch", "ads"];
    if (disabledTabsArray.includes(params.selectedTab)) {
        return true;
    }

    // TEMP: until data will be fixed
    const disabledPagesArray = [
        "WebsitePaidLandingPages",
        "KeywordAnalysisV2Organic",
        "WebsiteKeywordsV2",
        "OrganicLandingPagesV2",
        "WebAnalysisSearchOverview",
        "AdIntelligenceMediaPartners",
        "AdIntelligenceWAOverview",
        "AIRefferrals",
    ];

    if (disabledPagesArray.includes(state.configId)) {
        return true;
    }

    const isWebsiteVirtual = params.key && params.key[0] === "*";
    if (isWebsiteVirtual) {
        return true;
    }
    const isMainDomainDisabled = swSettings.current?.resources?.IsMainDomainDisabled;
    if (isMainDomainDisabled) {
        return true;
    } else {
        const disabledSupportedPages = [
            {
                subSection: "traffic",
                subSubSection: "search",
            },
            {
                subSection: "content",
                subSubSection: "popular",
            },
            {
                subSection: "content",
                subSubSection: "subdomains",
            },
        ];
        const isMobileWeb = params.webSource && params.webSource === "MobileWeb";
        const matchedPage = _.find(disabledSupportedPages, {
            subSection: state.pageId.subSection,
            subSubSection: state.pageId.subSubSection,
        });
        if (matchedPage && isMobileWeb) {
            return true;
        } else {
            return false;
        }
    }
};

const displayPagesWebSources = () => {
    return [commonWebSources.desktop(false)];
};

const searchPagesWebSources = (params: FixmeType) => {
    const hasMobileWebSearchPermission = swSettings.components.MobileWebSearch.isAllowed;
    const available = isHasMobileWebDataIgnoreDuration(params.country, "MobileWebSearch");
    const enabled = available && hasMobileWebSearchPermission;
    return [commonWebSources.desktop(), commonWebSources.mobileWeb(!enabled, available)];
};

// TODO: the new overview page should have total only
const searchOverviewPageWebSources = (params: FixmeType) => {
    const hasMobileWebSearchPermission = swSettings.components.MobileWebSearch.isAllowed;
    const available = isHasMobileWebDataIgnoreDuration(params.country, "MobileWebSearch");
    const hasTotalSearchPermission = swSettings.components.MarketingMixTotal.isAllowed;
    const availableTotal = isHasMobileWebData(params.duration, params.country, "MobileMarketingMix");

    const enabled = available && hasMobileWebSearchPermission;
    const enabledTotal = availableTotal && hasTotalSearchPermission;
    return [
        commonWebSources.total(!enabledTotal, availableTotal),
        commonWebSources.desktop(),
        commonWebSources.mobileWeb(!enabled, available),
    ];
};

const websiteSearchKeywordsWebSources = (params: FixmeType) => {
    const hasMobileWebSearchPermission = swSettings.components.MobileWebSearch.isAllowed;
    const isEnabled =
        isHasMobileWebDataIgnoreDuration(params.country, "MobileWebSearch") && hasMobileWebSearchPermission;
    const isAvailable = isHasMobileWebData(params.duration, params.country, "MobileWebSearch");
    return [commonWebSources.desktop(), commonWebSources.mobileWeb(!isEnabled, isAvailable)];
};

const keywords2WebSources = (params: FixmeType) => {
    const hasMobileWebPermission = swSettings.components.KeywordAnalysisMobileTraffic2.isAllowed;
    const isEnabled =
        isHasMobileWebDataIgnoreDuration(params.country, "KeywordAnalysisMobileTraffic2") && hasMobileWebPermission;
    const isAvailable = isHasMobileWebData(params.duration, params.country, "KeywordAnalysisMobileTraffic2");

    return [
        commonWebSources.total(!isEnabled, isAvailable),
        commonWebSources.desktop(false),
        commonWebSources.mobileWeb(!isEnabled, isAvailable),
    ];
};

const websitePerformanceWebSources = () => {
    return [commonWebSources.total()];
};

const trafficAndEngagementWebSources = () => {
    return [commonWebSources.total()];
};

const paidSearchOverview = (params: FixmeType) => {
    const hasMobileWebSearchPermission = swSettings.components.MobileWebSearch.isAllowed;
    const isEnabled =
        isHasMobileWebDataIgnoreDuration(params.country, "MobileWebSearch") && hasMobileWebSearchPermission;
    const isAvailable = isHasMobileWebData(params.duration, params.country, "MobileWebSearch");
    return [commonWebSources.desktop(), commonWebSources.mobileWeb(!isEnabled, isAvailable)];
};

const audienceInterestsPagesWebSources = (params: FixmeType) => {
    const { country } = params;
    const enabled = isMobileWebEnabled("MobileWeb", country);
    return [
        commonWebSources.total(!enabled, enabled),
        commonWebSources.desktop(),
        commonWebSources.mobileWeb(!enabled, enabled),
    ];
};

const industryPagesWithSegments = (params: FixmeType) => {
    const category = params.category;
    const categoryObject = categoryService.categoryQueryParamToCategoryObject(category);
    if (CategoryServiceUtils.hasSegments(categoryObject)) {
        return [commonWebSources.desktop()];
    }
    const hasMobileWebPermission = swSettings.components.MobileWeb.isAllowed;
    const available =
        swSettings.allowedDuration(params.duration, "MobileWeb") &&
        _.includes(swSettings.components.Home.resources.MobileWebCountries, parseInt(params.country));
    const enabled = available && hasMobileWebPermission;
    return [
        commonWebSources.total(!available, available),
        commonWebSources.desktop(available && !hasMobileWebPermission, true),
        commonWebSources.mobileWeb(!enabled, available),
    ];
};

const marketPageWithSegments = (params: FixmeType) => {
    const category = params.category;
    const categoryObject = categoryService.categoryQueryParamToCategoryObject(category);
    if (CategoryServiceUtils.hasSegments(categoryObject)) {
        return [commonWebSources.total()];
    }
    return totalOnlyPage(params);
};

const totalOnlyPage = (params: FixmeType) => {
    const hasMobileWebPermission = swSettings.components.MobileWeb.isAllowed;
    const available =
        swSettings.allowedDuration(params.duration, "MobileWeb") &&
        _.includes(swSettings.components.Home.resources.MobileWebCountries, parseInt(params.country));
    const enabled = available && hasMobileWebPermission;
    if (!enabled) {
        return [commonWebSources.desktop()];
    }
    return [commonWebSources.total()];
};

const popularPagesV2WebSources = () => {
    return [commonWebSources.total()];
};

const landingpages_v2 = (params: FixmeType, configId = "OrganicLandingPagesV2") => {
    const hasMobileWebPermission = swSettings.components[configId].isAllowed;
    const isEnabled = isHasMobileWebDataIgnoreDuration(params.country, configId) && hasMobileWebPermission;
    const isAvailable = isHasMobileWebData(params.duration, params.country, configId);

    return [
        commonWebSources.total(!isEnabled, isAvailable),
        commonWebSources.desktop(false),
        commonWebSources.mobileWeb(!isEnabled, isAvailable),
    ];
};

const manageWebsiteAnalysisPermissions = (params: { country: string; duration: string }, featureKey: string) => {
    const hasMobileWebPermission = swSettings.components[featureKey].isAllowed;
    const isEnabled = isHasMobileWebDataIgnoreDuration(params.country, featureKey) && hasMobileWebPermission;
    const isAvailable = isHasMobileWebData(params.duration, params.country, featureKey);

    return [
        commonWebSources.total(!isEnabled, isAvailable),
        commonWebSources.desktop(false),
        commonWebSources.mobileWeb(!isEnabled, isAvailable),
    ];
};

const adsDataV2WebSources = (params: FixmeType) => {
    if (!params.selectedPageTab || params.selectedPageTab === EAdsTabs[EAdsTabs.Text]) {
        return [commonWebSources.desktop(), commonWebSources.mobileWeb(false, true)];
    } else {
        return [commonWebSources.desktop()];
    }
};
const adsDataV2WebSources2 = (params: FixmeType) => {
    return [commonWebSources.total(), commonWebSources.desktop(), commonWebSources.mobileWeb()];
};

const pagesWebsources = {
    digitalsuite_website_referrals_incomingtraffic: () => [commonWebSources.total()],
    digitalsuite_website_marketingchannels: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_website_marketingchannels_beta: () => {
        return [commonWebSources.total()];
    },
    [WEBSITE_ANALYSIS_MARKETING_CHANNELS]: () => {
        return [commonWebSources.total()];
    },
    [WEBSITE_ANALYSIS_MARKETING_KEYWORDS]: (params: FixmeType) =>
        manageWebsiteAnalysisPermissions(params, "WebsiteKeywordsV2"),
    [WEBSITE_ANALYSIS_MARKETING_SEARCH_ADS]: adsDataV2WebSources2,
    paidsearch_website_ads: adsDataV2WebSources2,
    [searchAdvertisingRoutes.monitorKeywords]: () => [commonWebSources.total()],
    [searchAdvertisingRoutes.keywordLandingPages]: () => [
        commonWebSources.total(),
        commonWebSources.desktop(),
        commonWebSources.mobileWeb(),
    ],
    [searchAdvertisingRoutes.serpPlayers]: () => [
        commonWebSources.total(),
        commonWebSources.desktop(),
        commonWebSources.mobileWeb(),
    ],
    [searchAdvertisingRoutes.textAndShoppingAds]: () => [commonWebSources.total()],
    [searchAdvertisingRoutes.keywordAds]: () => [
        commonWebSources.total(),
        commonWebSources.desktop(),
        commonWebSources.mobileWeb(),
    ],
    [searchAdvertisingRoutes.topics]: () => [
        commonWebSources.total(),
        commonWebSources.desktop(),
        commonWebSources.mobileWeb(),
    ],
    paidsearch_keywordAnalysis_serp_ads: adsDataV2WebSources2,
    paidsearch_website_search_keyword: websiteSearchKeywordsWebSources,
    organicsearch_website_organic_search_overview: searchOverviewPageWebSources,
    organicsearch_websites_v2_traffic_keywords: (params: FixmeType) =>
        manageWebsiteAnalysisPermissions(params, "WebsiteKeywordsV2"),
    [searchAdvertisingRoutes.keywords]: () => [commonWebSources.total()],
    organicsearch_seo_overview: (params: FixmeType) => manageWebsiteAnalysisPermissions(params, "SEOOverview"),
    search_website_topics: (params: FixmeType) => manageWebsiteAnalysisPermissions(params, "WebsiteTopics"),
    organicsearch_website_search_phrases: searchPagesWebSources,
    organicsearch_website_search_ranking_distribution_v2: (params: FixmeType) =>
        manageWebsiteAnalysisPermissions(params, "RankingDistributionV2"),
    organicsearch_website_organiclandingpages_v2: landingpages_v2,
    organicsearch_website_landingpages_v2: landingpages_v2,
    [searchAdvertisingRoutes.landingPages]: () => [commonWebSources.total()],
    digitalsuite_marketresearch_webmarketanalysis_rankings_social: () => {
        return [commonWebSources.desktop()];
    },
    digitalsuite_marketresearch_webmarketanalysis_rankings_referrals: () => {
        return [commonWebSources.desktop()];
    },
    digitalsuite_marketresearch_webmarketanalysis_rankings_direct: () => {
        return [commonWebSources.desktop()];
    },
    digitalsuite_marketresearch_webmarketanalysis_rankings_mail: () => {
        return [commonWebSources.desktop()];
    },
    digitalsuite_marketresearch_webmarketanalysis_rankings_ads: () => {
        return [commonWebSources.desktop()];
    },
    digitalsuite_marketresearch_webmarketanalysis_rankings_search: () => {
        return [commonWebSources.desktop()];
    },
    digitalsuite_website_subdomains: (params: FixmeType) => {
        const hasSubDomainsPagesMobileWeb = swSettings.components.WebAnalysis.resources.HasSubDomainsPagesMobileWeb;
        const available = isHasMobileWebData(params.duration, params.country, "MobileWeb");
        const enabled = available && hasSubDomainsPagesMobileWeb;
        return [commonWebSources.desktop(), commonWebSources.mobileWeb(!enabled, available)];
    },
    accountreview_website_subdomains: (params: FixmeType) => {
        const hasSubDomainsPagesMobileWeb = swSettings.components.WebAnalysis.resources.HasSubDomainsPagesMobileWeb;
        const available = isHasMobileWebData(params.duration, params.country, "MobileWeb");
        const enabled = available && hasSubDomainsPagesMobileWeb;
        return [commonWebSources.desktop(), commonWebSources.mobileWeb(!enabled, available)];
    },
    digitalsuite_website_popular_v2: popularPagesV2WebSources,
    accountreview_website_website_structure_pages: popularPagesV2WebSources,
    digitalsuite_website_lob: () => {
        return [commonWebSources.total()];
    },
    [WEBSITE_ANALYSIS_PERFORMANCE]: websitePerformanceWebSources,
    digitalsuite_website_websiteperformance: websitePerformanceWebSources,
    companyresearch_segment_analysis_trafficandengagement: (params: FixmeType) => {
        const isMobileWebCustomSegmentsAnalysis = !!swSettings.components?.MobileWebCustomSegmentsAnalysis; // check if we should show the mobileWeb websource (in case that claim isMobileWebCustomSegmentsAnalysis exist)

        const available = isHasMobileWebData(params.duration, params.country, "MobileWebCustomSegmentsAnalysis");
        if (isMobileWebCustomSegmentsAnalysis) {
            return [
                commonWebSources.desktop(false),
                commonWebSources.mobileWeb(false, available, {
                    badgeType: BADGE_ITEMS.BETA,
                }),
            ];
        } else {
            return [commonWebSources.desktop(false)];
        }
    },

    [WEBSITE_ANALYSIS_TRAFFIC_ENGAGEMENT]: trafficAndEngagementWebSources,
    digitalsuite_website_trafficandengagement: trafficAndEngagementWebSources,
    digitalsuite_website_demographics: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_marketresearch_webmarketanalysis_geography: (params: FixmeType) => {
        const available = swSettings.allowedDuration(params.duration, "IndustryAnalysisGeography");
        return [
            commonWebSources.total(!available, available),
            commonWebSources.desktop(),
            commonWebSources.mobileWeb(!available, available),
        ];
    },
    market_research_geography: (params: FixmeType) => {
        const available = swSettings.allowedDuration(params.duration, "IndustryAnalysisGeography");
        if (!available) {
            return [commonWebSources.desktop()];
        }
        return [commonWebSources.total()];
    },
    market_research_geography_v2: () => {
        return [commonWebSources.total()];
    },
    [WEBSITE_ANALYSIS_GEOGRAPHY]: (params: FixmeType) => {
        const available = swSettings.allowedDuration(params.duration, "WebGeography");
        return [
            commonWebSources.total(!available, available),
            commonWebSources.desktop(),
            commonWebSources.mobileWeb(!available, available),
        ];
    },
    accountreview_website_audience_demographics: () => {
        return [commonWebSources.total()];
    },
    accountreview_website_website_structure_folders: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_website_display_ad_networks: displayPagesWebSources,
    digitalsuite_website_display_publishers: displayPagesWebSources,
    digitalsuite_website_display_overview: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_website_publisher_overview: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_website_display_videos: (params: FixmeType) => {
        const hasMobileWebPermission = swSettings.components.MobileWeb.isAllowed;
        const available = isHasMobileWebData(params.duration, params.country, "WebsiteAdsIntelVideo");
        const enabled = available && hasMobileWebPermission;

        return [
            commonWebSources.total(!available, available),
            commonWebSources.desktop(available && !hasMobileWebPermission, available || hasMobileWebPermission),
            commonWebSources.mobileWeb(!enabled, available),
        ];
    },
    accountreview_website_audience_interests: audienceInterestsPagesWebSources,
    digitalsuite_website_audienceInterests: audienceInterestsPagesWebSources,
    digitalsuite_website_audienceOverlap: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_marketresearch_webmarketanalysis_loyalty: () => {
        return [commonWebSources.total()];
    },
    market_research_demand_trends: () => {
        return [commonWebSources.total()];
    },
    market_research_demand_trends_v2: () => {
        return [commonWebSources.total()];
    },
    "industryAnalysis-categoryLeadersSearchAds": () => {
        return [commonWebSources.desktop(false)];
    },
    "industryAnalysis-categoryLeadersSearchReferrals": () => {
        return [commonWebSources.desktop(false)];
    },
    "industryAnalysis-categoryLeadersSearchMail": () => {
        return [commonWebSources.desktop(false)];
    },
    digitalsuite_marketresearch_webmarketanalysis_rankings: () => {
        return [commonWebSources.total()];
    },
    findkeywords_byindustry: () => {
        return [commonWebSources.desktop(false)];
    },
    findkeywords_byindustry_TopKeywords: () => {
        return [commonWebSources.desktop(false)];
    },
    "industryAnalysis-topKeywords": () => {
        return [commonWebSources.desktop(false)];
    },
    digitalsuite_findkeywords_keywordGeneratorTool: (params: FixmeType) => {
        return [commonWebSources.total()];
    },
    digitalsuite_monitorkeywords: (params: FixmeType) => {
        return [commonWebSources.total()];
    },
    digitalsuite_website_geography: (params: FixmeType) => {
        const available = swSettings.allowedDuration(params.duration, "WebGeography");
        return [
            commonWebSources.total(!available, available),
            commonWebSources.desktop(),
            commonWebSources.mobileWeb(!available, available),
        ];
    },
    digitalsuite_keywordAnalysis_geography: () => {
        return [commonWebSources.desktop()];
    },
    organicsearch_keywordAnalysis_geography: () => {
        return [commonWebSources.desktop()];
    },
    paidsearch_keywordAnalysis_geography: () => {
        return [commonWebSources.desktop()];
    },
    paidsearch_keywordAnalysis_overview: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_keywordAnalysis_serpSnapshot: () => {
        return [commonWebSources.desktop(), commonWebSources.mobileWeb(false, true)];
    },
    organicsearch_keywordAnalysis_organic_2: keywords2WebSources,
    organicsearch_keywordAnalysis_overview_2: keywords2WebSources,
    organicsearch_keywordAnalysis_2: keywords2WebSources,
    organicsearch_trafficAnalysis_2: keywords2WebSources,
    digitalsuite_website_referrals_outgoingtraffic: () => [commonWebSources.total()],
    digitalsuite_marketresearch_webmarketanalysis_overview: (params: FixmeType) => {
        const category = params.category;
        const categoryObject = categoryService.categoryQueryParamToCategoryObject(category);
        if (CategoryServiceUtils.hasSegments(categoryObject)) {
            return [commonWebSources.desktop()];
        }
        return defaultResolver("MobileWeb", params);
    },
    market_research_segmentation: totalOnlyPage,
    market_research_segmentation_v2: totalOnlyPage,
    market_research_overview: marketPageWithSegments,
    market_research_overview_v2: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    digitalsuite_marketresearch_webmarketanalysis_mapping: industryPagesWithSegments,
    digitalsuite_webmarketanalysis_home: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    market_research_players: marketPageWithSegments,
    market_research_players_v2: marketPageWithSegments,
    digitalsuite_marketresearch_webmarketanalysis_trends: (params: FixmeType) => {
        const category = params.category;
        const categoryObject = categoryService.categoryQueryParamToCategoryObject(category);
        if (CategoryServiceUtils.hasSegments(categoryObject)) {
            return [commonWebSources.desktop()];
        }
        return defaultResolver("MobileWeb", params);
    },
    market_research_traffic_channels: (params: FixmeType) => {
        return [commonWebSources.total()];
    },
    market_research_traffic_channels_v2: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_marketresearch_webmarketanalysis_demographics: (params: FixmeType) => {
        return [commonWebSources.total()];
    },
    market_research_demography: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    market_research_demography_v2: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    digitalsuite_marketresearch_keywordmarketanalysis_total: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_marketresearch_keywordmarketanalysis_geo: () => {
        return [commonWebSources.total()];
    },

    digitalsuite_marketresearch_keywordmarketanalysis_demand_search_trends: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_marketresearch_keywordmarketanalysis_topics_heatmap: () => [commonWebSources.total()],

    digitalsuite_website_search_overview: () => {
        return [commonWebSources.total()];
    },

    [WEBSITE_ANALYSIS_MARKETING_SEARCH_OVERVIEW]: () => {
        return [commonWebSources.total()];
    },
    "organicsearch_websites-competitorsKeywords": (_params: FixmeType) => {
        return [commonWebSources.total(false, true)];
    },
    paidsearch_website_search_paid_competitors: (params: FixmeType) => {
        const hasMobileWebSearchPermission = swSettings.components.MobileWebSearch.isAllowed;
        const available = isHasMobileWebDataIgnoreDuration(params.country, "MobileWebSearch");
        const enabled = available && hasMobileWebSearchPermission;
        return [commonWebSources.desktop(), commonWebSources.mobileWeb(!enabled, available)];
    },
    [WEBSITE_ANALYSIS_MARKETING_REFERRAL_INCOMING]: () => [commonWebSources.total()],
    [WEBSITE_ANALYSIS_MARKETING_REFERRAL_OUTGOING]: () => [commonWebSources.total()],
    [WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW]: () => [commonWebSources.total()],
    [WEBSITE_ANALYSIS_MARKETING_PUBLISHER_OVERVIEW]: () => [commonWebSources.total()],
    [WEBSITE_ANALYSIS_MARKETING_DISPLAY_AD_NETWORKS]: displayPagesWebSources,
    [WEBSITE_ANALYSIS_MARKETING_SOCIAL_OVERVIEW]: () => {
        return [commonWebSources.desktop()];
    },
    digitalsuite_website_social_overview: () => {
        return [commonWebSources.total()];
    },
    digitalsuite_websites_paidoutgoing_monetization_networks: () => {
        return [commonWebSources.desktop(false)];
    },
    digitalsuite_websites_paidoutgoing_advertisers: () => {
        return [commonWebSources.desktop(false)];
    },
    "digitalsuite_segments-analysis-traffic": () => {
        return [commonWebSources.total()];
    },
    "digitalsuite_segments-analysis-marketingChannels": () => {
        return [commonWebSources.total()];
    },
    "digitalsuite_segments-analysis-geography": () => {
        return [commonWebSources.total()];
    },
    "digitalsuite_segments-buyer_journey": (_params: FixmeType) => {
        return [commonWebSources.desktop()];
    },
    "activation-benchmarkTracker-report": (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    digitalsuite_website_folders: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    "adintelligence.root.frame.advertisersOverview": (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    "adintelligence.root.frame.advertisersCampaigns": (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    "adintelligence.root.frame.advertisersPublishers": (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    [advertiserActivityRoutes.overview]: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    [displayAdvertisingRoutes.overview.stateName]: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    [publisherAnalysisRoutes.overview]: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    [displayAdvertisingRoutes.campaigns.stateName]: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    [displayAdvertisingRoutes.ads.stateName]: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    [displayAdvertisingRoutes.publishers.stateName]: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    [publisherAnalysisRoutes.advertisers]: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    [publisherAnalysisRoutes.ads]: (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
    [displayAdvertisingRoutes.adNetworks.stateName]: (_params: FixmeType) => {
        return [commonWebSources.desktop()];
    },
    [publisherAnalysisRoutes.adNetworks]: (_params: FixmeType) => {
        return [commonWebSources.desktop()];
    },
    "adintelligence.root.frame.publishersOverview": (_params: FixmeType) => {
        return [commonWebSources.total()];
    },
};

export const getAvailableWebSource = (state: FixmeType, params: FixmeType) => {
    if (pagesWebsources.hasOwnProperty(state.name)) {
        // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — dynamic property access on pagesWebsources object
        return pagesWebsources[state.name](params).filter((item: FixmeType) => item.available);
    } else {
        return [];
    }
};

const getSortingArrayByType = (sortingType: ESortingType, array: ICountryObject[]): ICountryObject[] => {
    switch (sortingType) {
        case ESortingType.NAME:
            return array.sort((a, b) => (a.text >= b.text ? 1 : -1));
        case ESortingType.DATA:
            // @ts-ignore FIXME: strict migration - 'data' is possibly 'undefined' — sorting logic relies on runtime data existence
            return array.sort((a, b) => (a.data <= b.data ? 1 : -1));
        default:
            return array;
    }
};
export const getCountries = (
    excludeStates = false,
    component = swSettings.current,
    sortingType: ESortingType | null = null,
): any[] => {
    const { routing } = SwRedux.getInstance().getState();
    const { countryFilter } = SwRedux.getInstance().getState();
    const permitted: FixmeType[] = [];
    const unpermitted: FixmeType[] = [];
    const moduleAllowedCountries: number[] = component.resources.Countries;
    const mobileWebAllowedCountries: any = component.resources.MobileWebCountries;
    component.totalCountries.forEach((country: ICountryObject) => {
        let countryData = undefined;
        if (countryFilter?.countriesData !== null) {
            countryData = countryFilter.countriesData[country.id] ? countryFilter.countriesData[country.id] : 0;
        }
        const isMobileWeb = mobileWebAllowedCountries ? mobileWebAllowedCountries.indexOf(country.id) > -1 : false;
        const countryObj = {
            ...country,
            children: [],
            states: [],
            permitted: _.includes(moduleAllowedCountries, country.id),
            showDeviceIcon:
                ["apps", "keywords", "appcategory"].indexOf(routing.currentModule) === -1 && country.id !== 999,
            mobileWeb: isMobileWeb,
            tooltipText: isMobileWeb
                ? `${i18nFilter()("countryFilter.mobileWeb")}`
                : `${i18nFilter()("countryFilter.desktopOnly")}`,
            key: country.id,
            data: countryData,
        };
        if (countryObj.permitted) {
            permitted.push(countryObj);
        } else {
            unpermitted.push(countryObj);
        }
        if (!excludeStates) {
            if (country.children && country.children.length !== 0) {
                (country.children as any[]).forEach((child) => {
                    const countryObj = {
                        ...child,
                        children: [],
                        states: [],
                        key: child.id,
                        permitted: _.includes(moduleAllowedCountries, country.id),
                        isChild: true,
                        showDeviceIcon: false,
                    };
                    if (countryObj.permitted) {
                        permitted.push(countryObj);
                    } else {
                        unpermitted.push(countryObj);
                    }
                });
            }
        }
    });
    const getSortedCountryForPermittedAndNonPermitted = (
        permitted: ICountryObject[],
        unpermitted: ICountryObject[],
    ): ICountryObject[] => {
        permitted.length && unpermitted.length > 0
            ? (permitted[permitted.length - 1].isLastPermitted = true)
            : _.noop();
        return [...permitted, ...unpermitted];
    };

    let sortedCountries = unpermitted.length > 0 ? [...permitted] : [...permitted, ...unpermitted];
    if (unpermitted.length > 0 && sortingType) {
        const sortedPermitted = getSortingArrayByType(sortingType, permitted);
        const sortedUnPermitted = getSortingArrayByType(sortingType, unpermitted);
        sortedCountries = getSortedCountryForPermittedAndNonPermitted(sortedPermitted, sortedUnPermitted);
    } else {
        // @ts-ignore FIXME: strict migration - Argument of type 'null' is not assignable to parameter of type 'ESortingType' — null check handled at runtime
        sortedCountries = getSortingArrayByType(sortingType, sortedCountries);
        sortedCountries = getSortedCountryForPermittedAndNonPermitted(sortedCountries, unpermitted);
    }

    return sortedCountries;
};

export const getSelectedId = (obj: { id: string | number }) => {
    if (obj) {
        return {
            [obj.id]: true,
        };
    } else {
        return null;
    }
};

export const FiltersEnum = {
    ENABLED: "enabled",
    DISABLED: "disabled",
    HIDDEN: "hidden",
};
